import { Defect } from '../defects';
import { BaseModel } from '..';
import { Operator } from '../userSettings';
import { FuelUnit, OilUnit } from '../flights';
import { FeatureFlag } from '../featureFlags';

export const initialState: AircraftState = {
  aircraftMap: new Map(),
  fleetAircraftMap: new Map(),
  newAircraftId: null,
  lastFetched: 0,
  ttl: 30000,
  data: {
    list: [],
  },
  dirty: false,
};

export enum AircraftActionTypes {
  SAVE = 'aircraft/save',
  SAVE_ALL = 'aircraft/saveAll',
  SAVE_NEW = 'aircraft/saveNew',
  SAVE_STATUS = 'aircraft/saveStatus',
  REMOVE = 'aircraft/removePerson',
  ADD_PEOPLE = 'aircraft/adPeople',
  SAVE_FLEET = 'aircraft/saveFleet',
}

export enum AircraftResource {
  ADDITIONAL_CREW = 'AdditionalCrew',
  AIRCRAFT = 'Aircraft',
  DAMAGE_MAP = 'DamageMap',
  DEFECT = 'Defect',
  DOCUMENT = 'Document',
  FLIGHT = 'Flight',
  LOGBOOK = 'Logbook',
  RELEASE = 'Release',
  SCHEDULED_MX_ITEM = 'ScheduledMxItem',
  TRIP = 'Trip',
  WORKPACK = 'Workpack',
  INTERMITTENT_FAULT = 'IntermittentFault',
}

export enum AircraftPermission {
  CREATE = 'to_create',
  READ = 'to_read',
  UPDATE = 'to_update',
  DELETE = 'to_delete',
}
export interface SaveAircraftAction {
  type: AircraftActionTypes.SAVE;
  payload: Aircraft;
}

export interface SaveAllAircraftAction {
  type: AircraftActionTypes.SAVE_ALL;
  payload: Aircraft[];
}

export interface SaveFleetAircraftAction {
  type: AircraftActionTypes.SAVE_FLEET;
  payload: Aircraft[];
}

export interface SaveNewAircraftAction {
  type: AircraftActionTypes.SAVE_NEW;
  payload: Aircraft;
}

export interface SaveStatusAircraftAction {
  type: AircraftActionTypes.SAVE_STATUS;
  payload: {
    status: string;
    id: string;
  };
}

export interface AddPeopleAircraftAction {
  type: AircraftActionTypes.ADD_PEOPLE;
  payload: {
    people: People[];
    id: string;
  };
}

export interface RemoveAircraftAction {
  type: AircraftActionTypes.REMOVE;
  payload: Aircraft;
}

type AircraftAction =
  | SaveAircraftAction
  | SaveAllAircraftAction
  | SaveFleetAircraftAction
  | SaveNewAircraftAction
  | SaveStatusAircraftAction
  | AddPeopleAircraftAction
  | RemoveAircraftAction;

export const isCompleteAircraftRecord = (record: BaseAircraft | Aircraft): record is Aircraft =>
  record &&
  'departure_airport' in record &&
  'destination_airport' in record &&
  'totals' in record &&
  'tiles' in record;

export default function aircraftReducer(state = initialState, action: AircraftAction): AircraftState {
  switch (action.type) {
    case AircraftActionTypes.SAVE: {
      const aircraftMap = new Map(state.aircraftMap.entries());
      const stateAircraft = aircraftMap.get(action.payload.id);
      aircraftMap.set(action.payload.id, {
        ...stateAircraft,
        ...action.payload,
        lastFetched: Date.now(),
      });
      return {
        ...state,
        aircraftMap,
      };
    }
    case AircraftActionTypes.SAVE_STATUS: {
      const aircraftMap = new Map(state.aircraftMap.entries());
      const stateAircraft = aircraftMap.get(action.payload.id);
      aircraftMap.set(action.payload.id, {
        ...stateAircraft,
        ...action.payload,
        lastFetched: Date.now(),
      });
      return {
        ...state,
        aircraftMap,
        dirty: true,
      };
    }
    case AircraftActionTypes.SAVE_NEW: {
      const aircraftMap = new Map(state.aircraftMap.entries());
      const newAircraftId = action.payload.id;
      aircraftMap.set(action.payload.id, {
        ...action.payload,
        lastFetched: Date.now(),
      });
      return {
        ...state,
        newAircraftId,
        aircraftMap,
      };
    }
    case AircraftActionTypes.SAVE_ALL: {
      const aircraftMap = new Map(state.aircraftMap.entries());
      action.payload.forEach((aircraft: Aircraft | BaseAircraft) => {
        const stateAircraft = aircraftMap.get(aircraft.id);
        const lastFetched = isCompleteAircraftRecord(aircraft) ? Date.now() : stateAircraft?.lastFetched || 0;
        aircraftMap.set(aircraft.id, {
          ...stateAircraft,
          ...aircraft,
          lastFetched,
        });
      });
      return {
        ...state,
        aircraftMap,
        lastFetched: Date.now(),
        dirty: false,
      };
    }
    case AircraftActionTypes.SAVE_FLEET: {
      const aircraftMap = new Map(state.aircraftMap.entries());
      const fleetMap = new Map(state.fleetAircraftMap.entries());

      action.payload.forEach((aircraft: Aircraft | BaseAircraft) => {
        const stateAircraft = aircraftMap.get(aircraft.id);

        fleetMap.set(aircraft.id, aircraft);
        aircraftMap.set(aircraft.id, {
          ...stateAircraft,
          ...aircraft,
          lastFetched: Date.now(),
        });
      });
      return {
        ...state,
        fleetAircraftMap: fleetMap,
        aircraftMap,
        lastFetched: Date.now(),
        dirty: false,
      };
    }
    case AircraftActionTypes.ADD_PEOPLE: {
      const aircraftMap = new Map(state.aircraftMap.entries());
      const stateAircraft = aircraftMap.get(action.payload.id);
      aircraftMap.set(action.payload.id, {
        ...stateAircraft,
        aircraft_people: action.payload.people,
        lastFetched: Date.now(),
      });
      return {
        ...state,
        aircraftMap,
      };
    }
    case AircraftActionTypes.REMOVE: {
      const aircraftMap = new Map(state.aircraftMap.entries());
      aircraftMap.delete(action.payload.id);
      return {
        ...state,
        aircraftMap,
      };
    }
    default:
      return state;
  }
}

export interface AircraftState {
  aircraftMap: Map<string, Aircraft | BaseAircraft>;
  fleetAircraftMap: Map<string, Aircraft | BaseAircraft>;
  newAircraftId?: string;
  lastFetched: number;
  ttl: number;
  data: {
    list: Aircraft[];
  };
  dirty: boolean;
}

export interface AircraftType {
  id: string;
  manufacturer: string;
  model: string;
  designator: string;
  category: string;
  engine_count: number;
  engine_type: string;
  weight_category: string;
  aircraft_image_url: string;
}

export interface SimpleAircraft {
  id: string;
  clickable: boolean;
  fleet_number: string;
  locked: boolean;
  registration: string;
}

export interface MxThresholds {
  cycles?: number;
  days?: number;
  flights_seconds?: number;
  apu_seconds?: number;
  flight_seconds?: number;
  landings?: number;
  checks?: {
    [key: string]: {
      open: number;
      title: string;
      close_to_expiry: number;
      requires_flight?: boolean;
    };
  };
}

export interface StandardFieldConfig {
  enabled?: boolean;
  name_override?: string;
  long_name_override?: string;
  required?: boolean;
  override?: boolean;
  count?: number;
  format?: string;
  dropdown_enabled?: boolean;
  dropdown_options_available?: string[];
  dropdown_options_selected?: string[];
  reason_to_defer_dropdown_options?: string[];
  allowed_fuel_types?: string[];
  mixed_fuels?: Record<string, string>;
  enabled_array?: string[];
  display_initial?: boolean;
  start_offset?: number;
  end_offset?: number;
  locations?: string[];
  error_threshold?: number;
  show_widget?: boolean;
}


export interface StandardFields {
  [key: string]: StandardFieldConfig;
}

export enum StandardFieldKey {
  FUEL_ADJUSTMENT = 'fuel_adjustment',
  FUEL_BURN_DIFFERENCE = 'fuel_burn_difference',
}

export interface AppSettings {
  sync_limits?: {
    trips?: number;
    defects?: number;
    scheduled_mx_items?: number;
    intermittent_faults?: number;
    cabin_issues?: number;
  };

  workpacks_require_approval?: boolean;
  ignore_tolerance_for_repeats?: boolean;
  send_reminder_emails?: boolean;
  hide_engine_cycles_fields?: boolean;
  hide_airframe_cycles_fields?: boolean;
  maintenance_header?: boolean;
  frat_enabled?: boolean;
  default_callsign?: string;
  specific_gravity?: number;
  start_ddl_page?: number;
  start_ddl_number?: number;
  engine_oil_consumption_threshold?: string;
  apu_oil_consumption_threshold?: string;
}

export interface BaseAircraft extends BaseModel {
  id: string;
  serial_number: string;
  registration: string;
  year: number;
  simulator: false;
  apu_installed: boolean;
  apu_mode: string;
  operator_id: string;
  max_fuel_value: number;
  fuel_unit: FuelUnit;
  oil_unit: OilUnit;
  fluid_uplifts_recorded?: boolean;
  maintenance_status: string;
  flight_status: string;
  feature_flags: FeatureFlag[];
  mx_display_days: null;
  multipilot_rated: boolean;
  mx_thresholds: MxThresholds;
  aircraft_type: AircraftType;
  camp_integration_enabled: boolean;
  camp_last_synced: string;
  standard_fields?: StandardFields;
  app_settings?: AppSettings;
  app_permissions: AppPermission;
  fleet_number?: string;
  srp_template_id?: string;
  jl_template_id?: string;
  login?: boolean;
  billing_status?: string;
  locked?: boolean;
  custom_fields?: CustomFields;
  max_weight?: number;
  flight_risk_assessment_id?: string;
  hydraulic_circuits?: {
    hydraulic_circuit_id: string;
    hydraulic_circuit_name: string;
  }[];
  active_aircraft_drawing?: {
    id: string;
    name: string;
    version: number;
  };
  issue_types?: IssueType[];
}

export const SUPPORTED_CUSTOM_FIELDS = ['boolean', 'selector', 'number', 'string', 'text'];

export interface CustomFields {
  [key: string]: CustomField;
}

export interface CustomField {
  heading: string;
  title: string;
  type: CustomFieldTypes;
  view: ViewTypes;
  key: string;
  order?: number;
  insert_after?: string;
  options?: string[];
  process_order?: number;
  precision?: number;
  min?: number | string;
  max?: number | string;
  unit?: string;
  show_only?: string[];
  default?: string;
  mode?: string;
  interval?: number;
  display_previous_value?: boolean;
  required?: boolean;
  show_last_time_completed_on_preflight?: boolean;
}

export enum ViewTypes {
  PREFLIGHT = 'preflight',
  POSTFLIGHT = 'postflight',
  MXCHECK = 'mx_check',
  TRIP = 'trip',
  TRIPUPDATE = 'trip_update',
}

export enum CustomFieldTypes {
  NUMBER = 'number',
  TEXT = 'text',
  COUNTER = 'counter',
  STRING = 'string',
  BOOLEAN = 'boolean',
  ATTACHMENT = 'attachment',
  DATE = 'date',
  SELECTOR = 'selector',
  DURATION = 'duration',
}

export interface AppPermission {
  trip: {
    can_sign: string | string[];
    can_sign_scope_type: string;
  };
  preflight: {
    can_sign: string | string[];
    can_sign_scope_type: string;
  };
  postflight: {
    can_sign: string | string[];
    can_sign_scope_type: string;
  };
  mx_check?: {
    can_sign: string | string[];
    can_sign_scope_type: string;
  };
  crs?: {
    can_sign: string | string[];
    can_sign_scope_type: string;
  };
  issue?: {
    can_sign: string | string[];
    can_sign_scope_type: string;
  };
  uplift?: {
    can_sign: string | string[];
    can_sign_scope_type: string;
  };
}

export interface Aircraft extends BaseAircraft {
  departure_airport: {
    ident: string | null;
    latitude: string | null;
    longitude: string | null;
    name: string | null;
  };
  destination_airport: {
    ident: string | null;
    latitude: string | null;
    longitude: string | null;
  };
  camp_integration_enabled: boolean;
  days_remaining: number;
  flight_seconds_remaining: number;
  apu_seconds_remaining: number;
  cycles_remaining: number;
  open_defects: Defect[];
  limitations: string[];
  estimated_next_due_date: string;
  aircraft_people: People[];
  maintenance_status_reason: string;
  missing_crs_workpack_id?: string;
  clickable: boolean;
  favourited: boolean;
  totals?: Record<string, Record<string, string | number>>;
}

export interface AircraftForPublicPage extends Aircraft {
  operator: Operator;
}

export interface People {
  first_name: string;
  last_name: string;
  avatar_url: string;
  email: string;
  position: string;
  last_flown: string;
}

export interface IssueType {
  aircraft_id: string;
  id: string;
  name: string;
  status: string;
  type: string;
}

export interface ServiceabilityFilters {
  ok: boolean;
  critical: boolean;
  overdue: boolean;
  unknown: boolean;
}

export interface AircraftStatusFilters {
  active: boolean;
  archived: boolean;
  deleted: boolean;
  demo: boolean;
  locked: boolean;
  'mel only': boolean;
  setup: boolean;
}

export interface FlightStatusFilters {
  on_ground: boolean;
  in_flight: boolean;
}

export const defaultWritePermissions = [
  {
    resource_type: AircraftResource.ADDITIONAL_CREW,
    to_read: true,
    to_create: true,
    to_update: true,
    to_delete: true,
  },
  {
    resource_type: AircraftResource.DAMAGE_MAP,
    to_read: true,
    to_create: true,
    to_update: true,
    to_delete: true,
  },
  {
    resource_type: AircraftResource.DEFECT,
    to_read: true,
    to_create: true,
    to_update: true,
    to_delete: true,
  },
  {
    resource_type: AircraftResource.DOCUMENT,
    to_read: true,
    to_create: true,
    to_update: true,
    to_delete: true,
  },
  {
    resource_type: AircraftResource.FLIGHT,
    to_read: true,
    to_create: true,
    to_update: true,
    to_delete: true,
  },
  {
    resource_type: AircraftResource.LOGBOOK,
    to_read: true,
    to_create: true,
    to_update: true,
    to_delete: true,
  },
  {
    resource_type: AircraftResource.RELEASE,
    to_read: true,
    to_create: true,
    to_update: true,
    to_delete: true,
  },
  {
    resource_type: AircraftResource.SCHEDULED_MX_ITEM,
    to_read: true,
    to_create: true,
    to_update: true,
    to_delete: true,
  },
  {
    resource_type: AircraftResource.TRIP,
    to_read: true,
    to_create: true,
    to_update: true,
    to_delete: true,
  },
  {
    resource_type: AircraftResource.WORKPACK,
    to_read: true,
    to_create: true,
    to_update: true,
    to_delete: true,
  },
];

export const defaultPilotPermissions = [
  {
    resource_type: AircraftResource.ADDITIONAL_CREW,
    to_read: true,
    to_create: false,
    to_update: false,
    to_delete: false,
  },
  {
    resource_type: AircraftResource.DAMAGE_MAP,
    to_read: true,
    to_create: false,
    to_update: false,
    to_delete: false,
  },
  {
    resource_type: AircraftResource.DEFECT,
    to_read: true,
    to_create: true,
    to_update: true,
    to_delete: true,
  },
  {
    resource_type: AircraftResource.DOCUMENT,
    to_read: true,
    to_create: false,
    to_update: false,
    to_delete: false,
  },
  {
    resource_type: AircraftResource.FLIGHT,
    to_read: true,
    to_create: true,
    to_update: true,
    to_delete: true,
  },
  {
    resource_type: AircraftResource.LOGBOOK,
    to_read: true,
    to_create: false,
    to_update: false,
    to_delete: false,
  },
  {
    resource_type: AircraftResource.RELEASE,
    to_read: true,
    to_create: false,
    to_update: false,
    to_delete: false,
  },
  {
    resource_type: AircraftResource.SCHEDULED_MX_ITEM,
    to_read: true,
    to_create: false,
    to_update: false,
    to_delete: false,
  },
  {
    resource_type: AircraftResource.TRIP,
    to_read: true,
    to_create: true,
    to_update: true,
    to_delete: true,
  },
  {
    resource_type: AircraftResource.WORKPACK,
    to_read: true,
    to_create: false,
    to_update: false,
    to_delete: false,
  },
];

export const defaultReadPermissions = [
  {
    resource_type: AircraftResource.ADDITIONAL_CREW,
    to_read: true,
    to_create: false,
    to_update: false,
    to_delete: false,
  },
  {
    resource_type: AircraftResource.DAMAGE_MAP,
    to_read: true,
    to_create: false,
    to_update: false,
    to_delete: false,
  },
  {
    resource_type: AircraftResource.DEFECT,
    to_read: true,
    to_create: false,
    to_update: false,
    to_delete: false,
  },
  {
    resource_type: AircraftResource.DOCUMENT,
    to_read: true,
    to_create: false,
    to_update: false,
    to_delete: false,
  },
  {
    resource_type: AircraftResource.FLIGHT,
    to_read: true,
    to_create: false,
    to_update: false,
    to_delete: false,
  },
  {
    resource_type: AircraftResource.LOGBOOK,
    to_read: true,
    to_create: false,
    to_update: false,
    to_delete: false,
  },
  {
    resource_type: AircraftResource.RELEASE,
    to_read: true,
    to_create: false,
    to_update: false,
    to_delete: false,
  },
  {
    resource_type: AircraftResource.SCHEDULED_MX_ITEM,
    to_read: true,
    to_create: false,
    to_update: false,
    to_delete: false,
  },
  {
    resource_type: AircraftResource.TRIP,
    to_read: true,
    to_create: false,
    to_update: false,
    to_delete: false,
  },
  {
    resource_type: AircraftResource.WORKPACK,
    to_read: true,
    to_create: false,
    to_update: false,
    to_delete: false,
  },
];

export const STATUS_COLOUR_MAP = new Map<string, string>([
  ['serviceable', '4aa91c'],
  ['unknown', 'd2d2d2'],
  ['overdue', 'f5222d'],
  ['unserviceable', 'f5222d'],
]);
